import TipTapLink from '@tiptap/extension-link'
import { mergeAttributes, markPasteRule } from '@tiptap/core'
import { find } from 'linkifyjs'
import { formatHardBreaksfromLinks } from './helpers'

export type LinkOptions = {
  openOnClick: boolean
  linkOnPaste: boolean
  HTMLAttributes: {
    target: string
    rel: string
    class: string | null
  }
  allowCustomAttributes: boolean
  unpublishedStories: string[]
  validate: (url: unknown) => boolean
}

const Link = TipTapLink.extend<LinkOptions>({
  addOptions() {
    return {
      ...this.parent?.(),
      openOnClick: false,
      linkOnPaste: true,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer nofollow',
        class: null,
      },
      allowCustomAttributes: false,
      unpublishedStories: [],
      // https://github.com/ueberdosis/tiptap/blob/1e562ec7dae682cc720619d87c91302442baeff5/packages/extension-link/src/link.ts#L147C7-L147C30
      validate: (url: unknown): boolean => !!url,
    }
  },
  // https://github.com/ueberdosis/tiptap/blob/1e562ec7dae682cc720619d87c91302442baeff5/packages/extension-link/src/link.ts#L218
  addPasteRules() {
    return [
      markPasteRule({
        find: (text) => {
          if (text) {
            const { validate } = this.options
            const links = find(text).filter(
              (item) => item.isLink && validate?.(item.value),
            )
            // NOTE: When a set of non bullet list type links are pasted into the editor they are automatically formatted with hard breaks.
            // This makes Tiptap format all links to look like one single link when there are multiple present.
            // This only occurs when the HardBreak extension is enabled.
            // This is a workaround to ensure that the links are formatted correctly.
            const formattedLinks = formatHardBreaksfromLinks(links)

            if (formattedLinks.length) {
              formattedLinks.forEach((link) => {
                this.editor?.commands?.setLink({
                  href: link.href,
                  target: '_blank',
                })
              })
            }
          }
          return []
        },
        type: this.type,
      }),
    ]
  },

  addAttributes() {
    const optionalAttributes: { custom?: { default: null } } = {}

    if (this.options.allowCustomAttributes) {
      optionalAttributes.custom = {
        default: null,
      }
    }

    return {
      href: {
        default: null,
      },
      uuid: {
        default: null,
      },
      linktype: {
        default: 'url',
      },
      target: {
        default: null,
      },
      anchor: {
        default: null,
      },
      ...optionalAttributes,
    }
  },

  // extended from https://github.com/ueberdosis/tiptap/blob/main/packages/extension-link/src/link.ts#L127
  renderHTML({ HTMLAttributes }) {
    const storyUUID = HTMLAttributes?.uuid
    const unpublishedStories = this.options.unpublishedStories

    let attributes = mergeAttributes(
      this.options.HTMLAttributes,
      HTMLAttributes,
    )
    // False positive; we're explicitly checking for javascript: links to ignore them
    // eslint-disable-next-line no-script-url
    if (HTMLAttributes.href?.startsWith('javascript:')) {
      attributes = mergeAttributes(this.options.HTMLAttributes, {
        ...HTMLAttributes,
        href: '',
      })
      return ['a', attributes, 0]
    }

    if (
      HTMLAttributes?.linktype === 'story' &&
      storyUUID &&
      unpublishedStories?.includes(storyUUID)
    ) {
      attributes = mergeAttributes(this.options.HTMLAttributes, {
        ...HTMLAttributes,
        class: 'story-warning',
      })
    }

    return ['a', attributes, 0]
  },
})

export default Link
